import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  boardCardAgingModePatchSchema,
  boardCardAgingModeSchema,
  cardDetailSchema,
  cardEvergreenPatchSchema,
  computeCardAgingStage,
  type ApiError,
  type BoardCardAgingMode,
  type BoardCardAgingModePatch,
  type BoardContents,
  type CardAgingMode,
  type CardAgingStage,
  type CardDetail,
  type CardEvergreenPatch,
} from '@molt/shared';
import { get, patch } from './api';
import { setBoardContentsQueries } from './cross-board-move';
import { queryKeys } from './query-keys';

export interface SetCardEvergreenVariables {
  readonly boardId: string;
  readonly cardId: string;
  readonly agingExempt: boolean;
}

type CardAgingCacheFields = Pick<CardDetail, 'agingExempt' | 'lastActivityAt'>;

export function getBoardCardAgingMode(boardId: string): Promise<BoardCardAgingMode> {
  return get(
    `/boards/${encodeURIComponent(boardId)}/card-aging-mode`,
    boardCardAgingModeSchema,
  );
}

export function updateBoardCardAgingMode(
  boardId: string,
  body: BoardCardAgingModePatch,
): Promise<BoardCardAgingMode> {
  return patch(
    `/boards/${encodeURIComponent(boardId)}/card-aging-mode`,
    boardCardAgingModeSchema,
    boardCardAgingModePatchSchema.parse(body),
  );
}

export function updateCardEvergreen(
  cardId: string,
  body: CardEvergreenPatch,
): Promise<CardDetail> {
  return patch(
    `/cards/${encodeURIComponent(cardId)}/aging-exempt`,
    cardDetailSchema,
    cardEvergreenPatchSchema.parse(body),
  );
}

export function cardAgingStageForMode(
  mode: CardAgingMode,
  fields: CardAgingCacheFields,
  now: string | number | Date = new Date(),
): CardAgingStage | null {
  if (fields.lastActivityAt === '') return null;
  return computeCardAgingStage({
    mode,
    exempt: fields.agingExempt,
    lastActivityAt: fields.lastActivityAt,
    now,
  });
}

export function applyBoardCardAgingModeToBoardContents(
  previous: BoardContents | undefined,
  state: BoardCardAgingMode,
  now: string | number | Date = new Date(),
): BoardContents | undefined {
  if (previous === undefined) return undefined;
  if (previous.board.id !== state.boardId) return previous;

  let changed = previous.board.cardAgingMode !== state.cardAgingMode;
  const lists = previous.lists.map((list) => {
    let listChanged = false;
    const cards = list.cards.map((card) => {
      const agingStage = cardAgingStageForMode(state.cardAgingMode, card, now);
      if (card.agingStage === agingStage) return card;
      changed = true;
      listChanged = true;
      return { ...card, agingStage };
    });

    return listChanged ? { ...list, cards } : list;
  });

  return changed
    ? {
        ...previous,
        board: { ...previous.board, cardAgingMode: state.cardAgingMode },
        lists,
      }
    : previous;
}

export function applyCardAgingFieldsToBoardContents(
  previous: BoardContents | undefined,
  cardId: string,
  fields: CardAgingCacheFields,
  now: string | number | Date = new Date(),
): BoardContents | undefined {
  if (previous === undefined) return undefined;

  let changed = false;
  const lists = previous.lists.map((list) => {
    let listChanged = false;
    const cards = list.cards.map((card) => {
      if (card.id !== cardId) return card;

      const agingStage = cardAgingStageForMode(previous.board.cardAgingMode, fields, now);
      if (
        card.agingExempt === fields.agingExempt &&
        card.lastActivityAt === fields.lastActivityAt &&
        card.agingStage === agingStage
      ) {
        return card;
      }

      changed = true;
      listChanged = true;
      return {
        ...card,
        agingExempt: fields.agingExempt,
        lastActivityAt: fields.lastActivityAt,
        agingStage,
      };
    });

    return listChanged ? { ...list, cards } : list;
  });

  return changed ? { ...previous, lists } : previous;
}

export function applyCardLastActivityToBoardContents(
  previous: BoardContents | undefined,
  cardId: string,
  lastActivityAt: string,
  now: string | number | Date = new Date(),
): BoardContents | undefined {
  if (previous === undefined) return undefined;

  let changed = false;
  const lists = previous.lists.map((list) => {
    let listChanged = false;
    const cards = list.cards.map((card) => {
      if (card.id !== cardId) return card;

      const agingStage = cardAgingStageForMode(
        previous.board.cardAgingMode,
        {
          agingExempt: card.agingExempt,
          lastActivityAt,
        },
        now,
      );
      if (card.lastActivityAt === lastActivityAt && card.agingStage === agingStage) {
        return card;
      }

      changed = true;
      listChanged = true;
      return { ...card, lastActivityAt, agingStage };
    });

    return listChanged ? { ...list, cards } : list;
  });

  return changed ? { ...previous, lists } : previous;
}

export function applyCardAgingDetailToBoardContents(
  previous: BoardContents | undefined,
  detail: CardDetail,
  now: string | number | Date = new Date(),
): BoardContents | undefined {
  return applyCardAgingFieldsToBoardContents(previous, detail.id, detail, now);
}

export function applyBoardCardAgingModeSuccess(
  queryClient: QueryClient,
  boardId: string,
  state: BoardCardAgingMode,
): void {
  const now = new Date();
  queryClient.setQueryData(queryKeys.boards.cardAgingMode(boardId), state);
  setBoardContentsQueries(queryClient, boardId, (previous) =>
    applyBoardCardAgingModeToBoardContents(previous, state, now),
  );
  void queryClient.invalidateQueries({
    queryKey: queryKeys.boards.cardAgingMode(boardId),
  });
  void queryClient.invalidateQueries({ queryKey: queryKeys.boards.contents(boardId) });
}

export function applyCardEvergreenSuccess(
  queryClient: QueryClient,
  boardId: string,
  detail: CardDetail,
): void {
  const now = new Date();
  queryClient.setQueryData(queryKeys.cards.detail(detail.id), detail);
  setBoardContentsQueries(queryClient, boardId, (previous) =>
    applyCardAgingDetailToBoardContents(previous, detail, now),
  );
  void queryClient.invalidateQueries({ queryKey: queryKeys.boards.contents(boardId) });
}

export function boardCardAgingModeQueryOptions(boardId: string) {
  return queryOptions({
    queryKey: queryKeys.boards.cardAgingMode(boardId),
    queryFn: () => getBoardCardAgingMode(boardId),
  });
}

export function useBoardCardAgingMode(boardId: string, enabled = true) {
  return useQuery({
    ...boardCardAgingModeQueryOptions(boardId),
    enabled,
  });
}

export function useUpdateBoardCardAgingMode(
  boardId: string,
): UseMutationResult<BoardCardAgingMode, ApiError, BoardCardAgingModePatch> {
  const queryClient = useQueryClient();

  return useMutation<BoardCardAgingMode, ApiError, BoardCardAgingModePatch>({
    mutationFn: (body) => updateBoardCardAgingMode(boardId, body),
    onSuccess: (state) => {
      applyBoardCardAgingModeSuccess(queryClient, boardId, state);
    },
  });
}

export function useSetCardEvergreen(): UseMutationResult<
  CardDetail,
  ApiError,
  SetCardEvergreenVariables
> {
  const queryClient = useQueryClient();

  return useMutation<CardDetail, ApiError, SetCardEvergreenVariables>({
    mutationFn: (variables) =>
      updateCardEvergreen(variables.cardId, { agingExempt: variables.agingExempt }),
    onSuccess: (detail, variables) => {
      applyCardEvergreenSuccess(queryClient, variables.boardId, detail);
    },
  });
}
